
import { Component, Ref, Vue } from 'vue-property-decorator'
import UnorderedList from '@/components/molecules/UnorderedList.vue'
import ListItem from '@/components/molecules/ListItem.vue'
import ModalInputDrawing from '@/components/templates/study/ModalInputDrawing.vue'
import { StudyModel } from '@/models/StudyModel'
import { DrawingModel } from '@/models/DrawingModel'
import MyScriptMath from '@/api/external/MyScriptMath'

/**
 * 解答入力モーダル(数式入力)
 */
@Component({
  components: {
    UnorderedList,
    ListItem,
    ModalInputDrawing,
  },
})
export default class ModalCorrectInputNumericlal extends Vue {
  /** モーダル要素 */
  @Ref()
  modalInputDrawing!: ModalInputDrawing

  /** 手書き入力変換(数式) */
  private myScriptMath: MyScriptMath = new MyScriptMath()

  /** 変換処理実行中フラグ */
  private isRecognizing = false

  /** 変換処理予約フラグ(API実行中に変換処理が必要になった場合trueになる) */
  private isReserveRecognizing = false

  /** 入力完了後に実行する処理 */
  private afterFunction?: StudyModel.AfterInputNumericalFormulaFunction

  /**
   * モーダルの表示処理
   *
   * @param question 設問情報
   * @param index 入力欄のインデックス
   * @param afterFunction 入力完了後の処理
   */
  public showModal(
    question: StudyModel.Question,
    index: number,
    afterFunction: StudyModel.AfterInputNumericalFormulaFunction
  ): void {
    // ローディング開始
    this.$loading.start()

    // 入力後処理の保存
    this.afterFunction = afterFunction

    // 変換処理フラグの初期化
    this.isRecognizing = false
    this.isReserveRecognizing = false

    // 設問情報取得
    const questionContent = question.contents[index] as StudyModel.ContentNumericalFormula

    // モーダル表示
    this.modalInputDrawing.show(
      question.managementLabel,
      questionContent.inputType,
      question.inputValues[index],
      questionContent.strokes
    )

    // ローディング終了
    this.$loading.complete()
  }

  /**
   * ストローク更新時の処理
   */
  private async updateStroke(): Promise<void> {
    // 既に変換中の場合は予約フラグを立てて終了する
    if (this.isRecognizing) {
      this.isReserveRecognizing = true
      return
    }

    // ストローク情報が空の場合は空文字列を設定し終了
    if (this.modalInputDrawing.strokes.length <= 0) {
      this.modalInputDrawing.updateDrawingValue('')
      this.isRecognizing = false
      this.isReserveRecognizing = false
      return
    }

    // 表示直後はDOM要素が展開されていないケースがあるため、存在しない場合は少し待ってから再実行
    if (!this.modalInputDrawing.drawingArea) {
      await this.wait(500)
      this.isRecognizing = false
      this.isReserveRecognizing = false
      await this.updateStroke()
      return
    }

    // 実行中フラグをONに
    this.isRecognizing = true

    // 変換処理を実行
    const result = await this.myScriptMath.recognize(
      this.modalInputDrawing.drawingArea.getCanvasWidth(),
      this.modalInputDrawing.drawingArea.getCanvasHeight(),
      this.modalInputDrawing.strokes
    )
    if (result) {
      this.modalInputDrawing.updateDrawingValue(result)
    }

    // フラグをOFFに変更
    this.isRecognizing = false

    // 予約されている場合は再実行
    if (this.isReserveRecognizing) {
      this.isReserveRecognizing = false
      await this.updateStroke()
    }
  }

  /**
   * 入力値確定時の処理
   *
   * @param inputValue 入力値
   * @param inputType 入力モード
   * @param strokes ストローク情報
   */
  private submitInput(inputValue: string, inputType: string, strokes: DrawingModel.Stroke[]): void {
    // undefinedブロックしているが、実際には必ず実行される
    if (!this.afterFunction) {
      return
    }
    this.afterFunction(inputValue, inputType, strokes)
  }

  /**
   * ウェイト処理
   *
   * @param ms ミリ秒
   */
  private async wait(ms: number): Promise<void> {
    await new Promise((resolve) => setTimeout(resolve, ms))
  }
}
